import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DocumentArrowUpIcon } from '@heroicons/react/24/solid';
import { Button } from '@praetorian-chariot/ui';
import { MutationStatus } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Dropzone, Files } from '@/components/Dropzone';
import { Input, Type } from '@/components/form/Input';
import { Modal } from '@/components/Modal';
import { Row } from '@/components/Row';
import { Table } from '@/components/table/Table';
import { Columns, TableActions } from '@/components/table/types';
import { SearchBar } from '@/components/ui/SearchBar';
import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { Credential, QueryFilter } from '@/types';
import { useMutation } from '@/utils/api';
import { cn } from '@/utils/classname';

enum CredentialType {
  BASIC = 'basic',
  RECORDING = 'recording',
  BEARER_TOKEN = 'bearer_token',
  API_KEY = 'api_key',
}

enum RefreshIntervalUnit {
  SECONDS = 'seconds',
  MINUTES = 'minutes',
  HOURS = 'hours',
}

interface RequestHeader {
  key: string;
  value: string;
}

// Separate form state interfaces for each credential type
interface BasicCredentialFormData {
  label: string;
  username: string;
  password: string;
}

interface RecordingCredentialFormData {
  label: string;
  file: File | null;
}

interface DynamicConfigState {
  tokenServiceUrl: string;
  requestMethod: 'post' | 'get';
  requestHeaders: RequestHeader[];
  requestBody: string;
  refreshInterval: number;
  refreshIntervalUnit: RefreshIntervalUnit;
  tokenLocation: string;
}

interface BearerTokenFormData {
  label: string;
  isDynamic: boolean;
  token: string;
  dynamicConfig: DynamicConfigState;
}

interface ApiKeyFormData {
  label: string;
  parameterName: string;
  isDynamic: boolean;
  apiKey: string;
  addTo: 'header' | 'query' | 'cookie';
  dynamicConfig: DynamicConfigState;
}

// Initial state constants
const BASIC_FORM_INITIAL: BasicCredentialFormData = {
  label: '',
  username: '',
  password: '',
};

const RECORDING_FORM_INITIAL: RecordingCredentialFormData = {
  label: '',
  file: null,
};

const DYNAMIC_CONFIG_INITIAL: DynamicConfigState = {
  tokenServiceUrl: '',
  requestMethod: 'post',
  requestHeaders: [],
  requestBody: '',
  refreshInterval: 60,
  refreshIntervalUnit: RefreshIntervalUnit.MINUTES,
  tokenLocation: '',
};

const BEARER_TOKEN_FORM_INITIAL: BearerTokenFormData = {
  label: '',
  isDynamic: false,
  token: '',
  dynamicConfig: DYNAMIC_CONFIG_INITIAL,
};

const API_KEY_FORM_INITIAL: ApiKeyFormData = {
  label: '',
  parameterName: '',
  isDynamic: false,
  apiKey: '',
  addTo: 'header',
  dynamicConfig: DYNAMIC_CONFIG_INITIAL,
};

// Type-safe submit payload discriminated union
type CredentialSubmitPayload =
  | { type: CredentialType.BASIC; data: BasicCredentialFormData }
  | {
      type: CredentialType.RECORDING;
      data: RecordingCredentialFormData & { file: File };
    }
  | { type: CredentialType.BEARER_TOKEN; data: BearerTokenFormData }
  | { type: CredentialType.API_KEY; data: ApiKeyFormData };

// =============================================================================
// Validation Functions
// =============================================================================

function validateBasicFormData(form: BasicCredentialFormData): string | null {
  if (!form.username || !form.password) {
    return 'Username and password are required';
  }
  return null;
}

function validateRecordingFormData(
  form: RecordingCredentialFormData
): string | null {
  if (!form.label.trim()) {
    return 'Label is required';
  }
  if (!form.file) {
    return 'Please select a recording file';
  }
  return null;
}

function validateDynamicConfig(config: DynamicConfigState): string | null {
  if (!config.tokenServiceUrl) {
    return 'Token Service URL is required';
  }
  if (!config.tokenLocation) {
    return 'Token Location is required';
  }
  return null;
}

function validateBearerTokenFormData(form: BearerTokenFormData): string | null {
  if (!form.label.trim()) {
    return 'Label is required';
  }
  if (form.isDynamic) {
    return validateDynamicConfig(form.dynamicConfig);
  } else {
    if (!form.token) {
      return 'Token is required';
    }
  }
  return null;
}

function validateApiKeyFormData(form: ApiKeyFormData): string | null {
  if (!form.label.trim()) {
    return 'Label is required';
  }
  if (!form.parameterName) {
    return 'Parameter Name is required';
  }
  if (form.isDynamic) {
    return validateDynamicConfig(form.dynamicConfig);
  } else {
    if (!form.apiKey) {
      return 'API Key is required';
    }
  }
  return null;
}

// =============================================================================
// API Parameter Builder Functions
// =============================================================================

function toSeconds(value: number, unit: RefreshIntervalUnit): number {
  switch (unit) {
    case RefreshIntervalUnit.SECONDS:
      return value;
    case RefreshIntervalUnit.MINUTES:
      return value * 60;
    case RefreshIntervalUnit.HOURS:
      return value * 3600;
    default:
      return value;
  }
}

function sanitizeHeaders(headers: RequestHeader[]): Record<string, string>[] {
  return headers.map(header => ({
    [header.key]: header.value,
  }));
}

function buildDynamicTokenConfigPayload(
  config: DynamicConfigState
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    request_url: config.tokenServiceUrl,
    request_method: config.requestMethod,
    refresh_interval_seconds: toSeconds(
      config.refreshInterval,
      config.refreshIntervalUnit
    ),
    extract_path: config.tokenLocation,
  };

  if (config.requestHeaders.length > 0) {
    payload.request_headers = sanitizeHeaders(config.requestHeaders);
  }

  if (config.requestBody) {
    payload.request_body = config.requestBody;
  }

  return payload;
}

function buildBearerTokenParameters(
  data: BearerTokenFormData
): Record<string, unknown> {
  const parameters: Record<string, unknown> = {
    label: data.label,
    auth_type: 'bearer_token',
  };

  if (data.isDynamic) {
    parameters.dynamic_token_config = buildDynamicTokenConfigPayload(
      data.dynamicConfig
    );
  } else {
    parameters.token = data.token;
  }

  return parameters;
}

function buildApiKeyParameters(data: ApiKeyFormData): Record<string, unknown> {
  const parameters: Record<string, unknown> = {
    label: data.label,
    auth_type: 'api_key',
    parameter_name: data.parameterName,
    api_key_destination: data.addTo,
  };

  if (data.isDynamic) {
    parameters.dynamic_token_config = buildDynamicTokenConfigPayload(
      data.dynamicConfig
    );
  } else {
    parameters.api_key = data.apiKey;
  }

  return parameters;
}

interface WebAuthenticationProps {
  asset: { key: string; burp_type?: string };
}

export const WebAuthentication: React.FC<WebAuthenticationProps> = ({
  asset,
}) => {
  const axios = useAxios();

  const isWebService = asset.burp_type === 'webservice';

  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'type' | 'form'>('type');
  const [selectedCredentials, setSelectedCredentials] = useState<string[]>([]);

  // Separate form states for each credential type
  const [selectedCredentialType, setSelectedCredentialType] =
    useState<CredentialType>(CredentialType.BASIC);
  const [basicForm, setBasicForm] =
    useState<BasicCredentialFormData>(BASIC_FORM_INITIAL);
  const [recordingForm, setRecordingForm] =
    useState<RecordingCredentialFormData>(RECORDING_FORM_INITIAL);
  const [bearerTokenForm, setBearerTokenForm] = useState<BearerTokenFormData>(
    BEARER_TOKEN_FORM_INITIAL
  );
  const [apiKeyForm, setApiKeyForm] =
    useState<ApiKeyFormData>(API_KEY_FORM_INITIAL);

  const {
    data: rawCredentials = [],
    status: credentialsStatus,
    error: credentialsError,
    invalidate: invalidateCredentials,
  } = useMy({
    resource: 'credential',
    query: {
      node: {
        labels: ['Credential'],
        filters: [
          {
            field: 'accountKey',
            operator: '=',
            value: asset.key,
          },
          ...((search
            ? [{ field: 'name', operator: 'CONTAINS', value: search }]
            : []) as QueryFilter[]),
        ],
      },
    },
  });

  const { mutateAsync: deleteCredential } = useMutation({
    mutationFn: async (credentialId: string) => {
      const response = axios.delete(`/broker`, {
        data: { CredentialID: credentialId, Type: 'burp-authentication' },
      });

      toast.promise(response, {
        loading: 'Deleting credential...',
        success: 'Credential deleted successfully',
        error: 'Failed to delete credential',
      });

      return (await response).data;
    },
    defaultErrorMessage: 'Failed to delete credential',
  });
  const { mutateAsync: addCredential, status: addCredentialStatus } =
    useMutation({
      mutationFn: async (payload: CredentialSubmitPayload) => {
        if (payload.type === CredentialType.BASIC) {
          const response = await axios.post(`/broker`, {
            Category: 'env-integration',
            Type: 'burp-authentication',
            Format: isWebService ? ['api-auth'] : ['token'],
            Operation: 'add',
            ResourceKey: asset.key,
            Parameters: {
              ...(isWebService ? { auth_type: 'basic' } : {}),
              label: payload.data.label || payload.data.username,
              username: payload.data.username,
              password: payload.data.password,
            },
          });
          return response.data;
        } else if (payload.type === CredentialType.RECORDING) {
          const fileContent = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsText(payload.data.file);
          });

          const response = await axios.post(`/broker`, {
            Category: 'env-integration',
            Type: 'burp-authentication',
            Format: ['file'],
            Operation: 'add',
            ResourceKey: asset.key,
            Parameters: {
              label: payload.data.label,
              script: fileContent,
            },
          });
          return response.data;
        } else if (payload.type === CredentialType.BEARER_TOKEN) {
          const parameters = buildBearerTokenParameters(payload.data);

          const response = await axios.post(`/broker`, {
            Category: 'env-integration',
            Type: 'burp-authentication',
            Format: ['api-auth'],
            Operation: 'add',
            ResourceKey: asset.key,
            Parameters: parameters,
          });
          return response.data;
        } else if (payload.type === CredentialType.API_KEY) {
          const parameters = buildApiKeyParameters(payload.data);

          const response = await axios.post(`/broker`, {
            Category: 'env-integration',
            Type: 'burp-authentication',
            Format: ['api-auth'],
            Operation: 'add',
            ResourceKey: asset.key,
            Parameters: parameters,
          });
          return response.data;
        }
      },
      defaultErrorMessage: 'Failed to add credential',
    });

  const columns = useMemo<Columns<Credential>>(
    () => [
      {
        label: 'Name',
        id: 'name',
        isExpandable: { minWidth: 200 },
        cell: (credential: Credential) => (
          <span className="text-chariot-text-primary font-medium">
            {credential.name}
          </span>
        ),
      },
      {
        label: 'Type',
        id: 'type',
        fixedWidth: 150,
        cell: (credential: Credential) => (
          <span className="text-chariot-text-primary">
            {credential.format?.includes('api-auth')
              ? 'Api token'
              : credential.format?.includes('file')
                ? 'Recorded Login'
                : 'User/Pass'}
          </span>
        ),
      },
      {
        label: 'Added By',
        id: 'username',
        fixedWidth: 120,
        cell: (credential: Credential) => (
          <span className="text-chariot-text-secondary">
            {credential.username}
          </span>
        ),
      },
    ],
    []
  );

  const getRowActions = (credential: Credential): TableActions => ({
    menu: {
      items: [
        {
          label: 'Delete',
          value: 'delete',
          icon: <TrashIcon className="size-4" />,
          onClick: () => handleDeleteCredential(credential),
        },
      ],
    },
  });

  const handleBulkDeleteCredentials = async () => {
    if (selectedCredentials.length === 0) return;

    try {
      const promises = Promise.all(
        selectedCredentials.map(index => {
          const credentialId = rawCredentials?.[Number(index)]?.credentialId;
          if (!credentialId) return;
          return deleteCredential(credentialId);
        })
      );
      toast.promise(promises, {
        loading: `Deleting ${selectedCredentials.length} credential${selectedCredentials.length > 1 ? 's' : ''}...`,
        success: `Successfully deleted ${selectedCredentials.length} credential${selectedCredentials.length > 1 ? 's' : ''}`,
        error: 'Failed to delete some credentials',
      });

      await promises;
      setSelectedCredentials([]);
      invalidateCredentials();
    } catch (error) {
      console.error('Bulk delete credentials error:', error);
    }
  };
  const handleDeleteCredential = async (credential: Credential) => {
    try {
      await deleteCredential(credential.credentialId);
      toast.success('Credential deleted successfully');
      invalidateCredentials();
    } catch {
      //
    }
  };

  return (
    <div className="h-full space-y-4">
      <div className="flex items-center justify-between">
        <SearchBar
          size="sm"
          className="h-fit w-64 shrink-0 p-1 pb-0"
          value={search}
          onChange={setSearch}
          placeholder="Search"
        />
        <Button
          label="Add Credential"
          startIcon={<PlusIcon className="size-4" />}
          onClick={() => setIsAddModalOpen(true)}
          size="sm"
          data-testid="add-credential-button"
        />
      </div>

      <CredentialActions
        selectedCredentials={selectedCredentials}
        onBulkDelete={handleBulkDeleteCredentials}
        hideActions={selectedCredentials.length === 0}
      />

      <Table
        name="credentials"
        columns={columns}
        data={rawCredentials}
        status={credentialsStatus}
        error={credentialsError}
        rowActions={getRowActions}
        selection={{
          value: selectedCredentials,
          onChange: setSelectedCredentials,
          selectAll: false,
        }}
        noData={{
          title: 'No credentials found',
          description:
            'Add a credential to get started with web authentication.',
        }}
      />

      <AddCredentialModal
        isWebService={isWebService}
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setTimeout(() => {
            setModalStep('type');
            setSelectedCredentialType(CredentialType.BASIC);
            setBasicForm(BASIC_FORM_INITIAL);
            setRecordingForm(RECORDING_FORM_INITIAL);
            setBearerTokenForm(BEARER_TOKEN_FORM_INITIAL);
            setApiKeyForm(API_KEY_FORM_INITIAL);
          }, 500);
        }}
        modalStep={modalStep}
        setModalStep={setModalStep}
        selectedType={selectedCredentialType}
        onTypeChange={setSelectedCredentialType}
        basicForm={basicForm}
        setBasicForm={setBasicForm}
        recordingForm={recordingForm}
        setRecordingForm={setRecordingForm}
        bearerTokenForm={bearerTokenForm}
        setBearerTokenForm={setBearerTokenForm}
        apiKeyForm={apiKeyForm}
        setApiKeyForm={setApiKeyForm}
        addCredentialStatus={addCredentialStatus}
        onSubmit={async payload => {
          try {
            const response = addCredential(payload);

            toast.promise(response, {
              loading: 'Adding credential...',
              success: 'Credential added successfully',
              error: 'Failed to add credential',
            });

            await response;
            invalidateCredentials();
            setIsAddModalOpen(false);
            setTimeout(() => {
              setModalStep('type');
              setSelectedCredentialType(CredentialType.BASIC);
              setBasicForm(BASIC_FORM_INITIAL);
              setRecordingForm(RECORDING_FORM_INITIAL);
              setBearerTokenForm(BEARER_TOKEN_FORM_INITIAL);
              setApiKeyForm(API_KEY_FORM_INITIAL);
            }, 500);
          } catch (error) {
            toast.error('Failed to add credential');
            console.error('Add credential error:', error);
          }
        }}
      />
    </div>
  );
};

interface AddCredentialModalProps {
  isWebService: boolean;
  isOpen: boolean;
  onClose: () => void;
  modalStep: 'type' | 'form';
  setModalStep: (step: 'type' | 'form') => void;
  selectedType: CredentialType;
  onTypeChange: (type: CredentialType) => void;
  basicForm: BasicCredentialFormData;
  setBasicForm: React.Dispatch<React.SetStateAction<BasicCredentialFormData>>;
  recordingForm: RecordingCredentialFormData;
  setRecordingForm: React.Dispatch<
    React.SetStateAction<RecordingCredentialFormData>
  >;
  bearerTokenForm: BearerTokenFormData;
  setBearerTokenForm: React.Dispatch<React.SetStateAction<BearerTokenFormData>>;
  apiKeyForm: ApiKeyFormData;
  setApiKeyForm: React.Dispatch<React.SetStateAction<ApiKeyFormData>>;
  onSubmit: (payload: CredentialSubmitPayload) => Promise<void>;
  addCredentialStatus: MutationStatus;
}

const AddCredentialModal: React.FC<AddCredentialModalProps> = ({
  isWebService,
  isOpen,
  onClose,
  modalStep,
  setModalStep,
  selectedType,
  onTypeChange,
  basicForm,
  setBasicForm,
  recordingForm,
  setRecordingForm,
  bearerTokenForm,
  setBearerTokenForm,
  apiKeyForm,
  setApiKeyForm,
  onSubmit,
  addCredentialStatus,
}) => {
  const handleSubmit = async () => {
    if (modalStep === 'type') {
      setModalStep('form');
      return;
    }

    let error: string | null = null;
    let payload: CredentialSubmitPayload | null = null;

    switch (selectedType) {
      case CredentialType.BASIC:
        error = validateBasicFormData(basicForm);
        if (!error) {
          payload = { type: CredentialType.BASIC, data: basicForm };
        }
        break;
      case CredentialType.RECORDING:
        error = validateRecordingFormData(recordingForm);
        if (!error && recordingForm.file) {
          payload = {
            type: CredentialType.RECORDING,
            data: { ...recordingForm, file: recordingForm.file },
          };
        }
        break;
      case CredentialType.BEARER_TOKEN:
        error = validateBearerTokenFormData(bearerTokenForm);
        if (!error) {
          payload = {
            type: CredentialType.BEARER_TOKEN,
            data: bearerTokenForm,
          };
        }
        break;
      case CredentialType.API_KEY:
        error = validateApiKeyFormData(apiKeyForm);
        if (!error) {
          payload = { type: CredentialType.API_KEY, data: apiKeyForm };
        }
        break;
      default:
        break;
    }

    if (error) {
      toast.error(error);
      return;
    }

    if (payload) {
      await onSubmit(payload);
    }
  };

  const handleBack = () => {
    if (modalStep === 'form') {
      setModalStep('type');
    }
  };

  const getModalTitle = () => {
    if (modalStep === 'type') {
      return 'Step 1: Select Authentication Method';
    }

    switch (selectedType) {
      case CredentialType.BASIC:
        return 'Step 2: Enter Credentials';
      case CredentialType.RECORDING:
        return 'Step 2: Upload Login Recording';
      case CredentialType.BEARER_TOKEN:
        return 'Step 2: Configure Bearer Token';
      case CredentialType.API_KEY:
        return 'Step 2: Configure API Key';
      default:
        return 'Add Credential';
    }
  };

  const getFooterText = () => {
    switch (modalStep) {
      case 'type':
        return 'Next';
      case 'form':
        return 'Add Credential';
      default:
        return 'Next';
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      size="xl"
      footer={{
        className: 'w-fit',
        text: getFooterText(),

        onClick: handleSubmit,
        isLoading: addCredentialStatus === 'pending',
        disabled:
          (modalStep === 'type' && !selectedType) ||
          addCredentialStatus === 'pending',
        left:
          modalStep === 'form' ? (
            <Button
              label="Back"
              onClick={handleBack}
              styleType="tertiary"
              size="lg"
              disabled={addCredentialStatus === 'pending'}
            />
          ) : undefined,
      }}
    >
      {modalStep === 'type' && (
        <CredentialTypeSelection
          isWebService={isWebService}
          selectedType={selectedType}
          onTypeChange={onTypeChange}
        />
      )}

      {modalStep === 'form' && selectedType === CredentialType.BASIC && (
        <BasicLoginForm formData={basicForm} onChange={setBasicForm} />
      )}

      {modalStep === 'form' && selectedType === CredentialType.RECORDING && (
        <RecordingForm formData={recordingForm} onChange={setRecordingForm} />
      )}

      {modalStep === 'form' && selectedType === CredentialType.BEARER_TOKEN && (
        <BearerTokenForm
          formData={bearerTokenForm}
          onChange={setBearerTokenForm}
        />
      )}

      {modalStep === 'form' && selectedType === CredentialType.API_KEY && (
        <ApiKeyForm formData={apiKeyForm} onChange={setApiKeyForm} />
      )}
    </Modal>
  );
};

interface CredentialTypeSelectionProps {
  isWebService: boolean;
  selectedType: CredentialType;
  onTypeChange: (type: CredentialType) => void;
}

const CredentialTypeSelection: React.FC<CredentialTypeSelectionProps> = ({
  isWebService,
  selectedType,
  onTypeChange,
}) => {
  const webApplicationOptions = [
    {
      type: CredentialType.BASIC,
      optionNumber: 1,
      title: 'Enter Credentials',
      description: 'Manually provide authentication details for account access',
      buttonText: 'Enter Credentials',
      testId: 'basic-login-option',
      additionalContent: null,
    },
    {
      type: CredentialType.RECORDING,
      optionNumber: 2,
      title: 'Upload Login Recording',
      description: 'Upload a saved authentication session for reuse',
      buttonText: 'Upload Sequence',
      testId: 'recording-option',
      additionalContent: (
        <div className="text-chariot-text-secondary text-sm">
          Supported formats: .json
        </div>
      ),
    },
  ];

  const webServiceOptions = [
    {
      type: CredentialType.BASIC,
      optionNumber: 1,
      title: 'Basic Authentication',
      description: 'Username and password authentication',
      buttonText: 'Configure Basic Auth',
      testId: 'basic-auth-option',
      additionalContent: null,
    },
    {
      type: CredentialType.BEARER_TOKEN,
      optionNumber: 2,
      title: 'Bearer Token',
      description:
        'Token-based authentication with fixed or dynamic token retrieval',
      buttonText: 'Configure Bearer Token',
      testId: 'bearer-token-option',
      additionalContent: null,
    },
    {
      type: CredentialType.API_KEY,
      optionNumber: 3,
      title: 'API Key',
      description: 'API key authentication in header, query, or cookie',
      buttonText: 'Configure API Key',
      testId: 'api-key-option',
      additionalContent: null,
    },
  ];

  const options = isWebService ? webServiceOptions : webApplicationOptions;

  return (
    <div className="space-y-6 pt-1">
      {options.map(option => (
        <div key={option.type} className="relative">
          <div className="absolute -top-3 left-4 z-10">
            <span className="bg-chariot-brand-primary text-chariot-text-primary rounded-md px-3 py-1 text-sm font-medium">
              Option {option.optionNumber}
            </span>
          </div>

          <div
            className={cn(
              'border-chariot-stroke cursor-pointer rounded-lg border-2 p-6 pt-8 transition-all duration-200',
              selectedType === option.type &&
                'border-chariot-brand-primary bg-chariot-background-secondary/50 shadow-lg'
            )}
            onClick={() => onTypeChange(option.type)}
            data-testid={option.testId}
          >
            <div className="space-y-4">
              <div>
                <h3 className="text-chariot-text-primary mb-2 text-lg font-bold">
                  {option.title}
                </h3>
                <p className="text-chariot-text-secondary text-base">
                  {option.description}
                </p>
              </div>

              <div className="pt-2">
                <span
                  className={cn(
                    'border-chariot-brand-primary text-chariot-brand-primary rounded-full border bg-transparent px-4 py-2 text-sm font-medium',
                    option.additionalContent && 'mb-3 inline-block'
                  )}
                >
                  {option.buttonText}
                </span>
                {option.additionalContent}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

interface BasicLoginFormProps {
  formData: BasicCredentialFormData;
  onChange: React.Dispatch<React.SetStateAction<BasicCredentialFormData>>;
}

const BasicLoginForm: React.FC<BasicLoginFormProps> = ({
  formData,
  onChange,
}) => {
  return (
    <div className="space-y-4">
      <Input
        label="Label"
        name="label"
        value={formData.label}
        onChange={e => onChange(prev => ({ ...prev, label: e.target.value }))}
        placeholder="(optional, defaults to username)"
        data-testid="label-input"
      />

      <Input
        label="Username"
        name="username"
        value={formData.username}
        onChange={e =>
          onChange(prev => ({ ...prev, username: e.target.value }))
        }
        placeholder="Username"
        required
        data-testid="username-input"
      />

      <Input
        label="Password"
        name="password"
        type={Type.PASSWORD}
        value={formData.password}
        onChange={e =>
          onChange(prev => ({ ...prev, password: e.target.value }))
        }
        placeholder="Password"
        required
        data-testid="password-input"
      />
    </div>
  );
};

interface RecordingFormProps {
  formData: RecordingCredentialFormData;
  onChange: React.Dispatch<React.SetStateAction<RecordingCredentialFormData>>;
}

const RecordingForm: React.FC<RecordingFormProps> = ({
  formData,
  onChange,
}) => {
  const handleFilesDrop = (files: Files<'arrayBuffer'>) => {
    const file = files[0]?.file || null;
    onChange(prev => ({ ...prev, file }));
  };

  return (
    <div className="space-y-4">
      <Input
        label="Label"
        name="label"
        value={formData.label}
        onChange={e => onChange(prev => ({ ...prev, label: e.target.value }))}
        placeholder="Enter a descriptive label"
        required
        data-testid="recording-label-input"
      />

      <div>
        <label className="text-chariot-text-primary mb-2 block text-sm font-medium">
          Recording File
        </label>
        <Dropzone
          type="arrayBuffer"
          className="mt-1"
          onFilesDrop={handleFilesDrop}
          accept={{
            'application/json': ['.json'],
          }}
          data-testid="recording-file-input"
        >
          <div className="flex flex-col items-center gap-3">
            <DocumentArrowUpIcon className="text-chariot-brand-secondary size-8" />
            <h2 className="text-lg leading-none font-bold">
              Drag File to Upload
            </h2>
            <p className="text-chariot-text-secondary text-sm">or</p>
            <Button
              styleType="primary"
              label="Browse Files"
              onClick={() => {}}
              size="sm"
            />
            {formData.file && (
              <div
                onClick={e => {
                  e.stopPropagation();
                  onChange(prev => ({ ...prev, file: null }));
                }}
              >
                <Button
                  styleType="rounded"
                  size="xs"
                  className="mt-4 cursor-pointer pl-3"
                  label={formData.file.name}
                  endIcon={<XMarkIcon className="size-4 stroke-[2px]" />}
                  onClick={() => {}}
                />
              </div>
            )}
          </div>
        </Dropzone>
      </div>

      <div className="bg-chariot-background-secondary rounded-lg p-3">
        <p className="text-chariot-text-secondary text-sm">
          <strong>Note:</strong>{' '}
          {`Recording files should be captured using Burp Suite's Recorder. Please see the `}
          <a
            href="https://portswigger.net/burp/documentation/scanner/authenticated-scanning/using-recorded-logins"
            target="_blank"
            rel="noopener noreferrer"
            className="text-chariot-brand-primary hover:text-chariot-brand-secondary underline"
          >
            Burp Suite documentation on authenticated scanning
          </a>{' '}
          for more information. Supported format: JSON
        </p>
      </div>
    </div>
  );
};

interface BearerTokenFormProps {
  formData: BearerTokenFormData;
  onChange: React.Dispatch<React.SetStateAction<BearerTokenFormData>>;
}

const BearerTokenForm: React.FC<BearerTokenFormProps> = ({
  formData,
  onChange,
}) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="text-chariot-text-primary mb-3 block text-sm font-medium">
          Choose your token type
        </label>
        <div className="flex gap-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="tokenType"
              checked={!formData.isDynamic}
              onChange={() => onChange(prev => ({ ...prev, isDynamic: false }))}
              className="text-chariot-brand-primary focus:ring-chariot-brand-primary"
            />
            <span className="text-chariot-text-primary text-sm">Fixed</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="tokenType"
              checked={formData.isDynamic}
              onChange={() => onChange(prev => ({ ...prev, isDynamic: true }))}
              className="text-chariot-brand-primary focus:ring-chariot-brand-primary"
            />
            <span className="text-chariot-text-primary text-sm">Dynamic</span>
          </label>
        </div>
      </div>

      {!formData.isDynamic ? (
        <div className="border-chariot-stroke space-y-4 rounded-lg border p-4">
          <h3 className="text-chariot-text-primary text-sm font-bold">
            Fixed token
          </h3>
          <p className="text-chariot-text-secondary text-sm">
            An access token that does not change. Manually configure the token.
          </p>

          <Input
            label="Label"
            name="label"
            value={formData.label}
            onChange={e =>
              onChange(prev => ({ ...prev, label: e.target.value }))
            }
            placeholder="Enter label"
            data-testid="bearer-label-input"
          />

          <Input
            label="Token"
            name="token"
            type={Type.PASSWORD}
            value={formData.token}
            onChange={e =>
              onChange(prev => ({ ...prev, token: e.target.value }))
            }
            placeholder="Enter bearer token"
            required
            data-testid="bearer-token-input"
          />
        </div>
      ) : (
        <div className="border-chariot-stroke space-y-4 rounded-lg border p-4">
          <h3 className="text-chariot-text-primary text-sm font-bold">
            Dynamic token
          </h3>
          <p className="text-chariot-text-secondary text-sm">
            Automatically retrieve and refresh the token from a service.
          </p>

          <Input
            label="Label"
            name="label"
            value={formData.label}
            onChange={e =>
              onChange(prev => ({ ...prev, label: e.target.value }))
            }
            placeholder="Enter label"
            data-testid="bearer-label-dynamic-input"
          />

          <Input
            label="Token Service URL"
            name="tokenServiceUrl"
            value={formData.dynamicConfig.tokenServiceUrl}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  tokenServiceUrl: e.target.value,
                },
              }))
            }
            placeholder="https://api.example.com/token"
            required
            data-testid="token-service-url-input"
          />

          <Input
            label="Request Method"
            name="requestMethod"
            type={Type.SELECT}
            value={formData.dynamicConfig.requestMethod}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  requestMethod: e.target.value as 'post' | 'get',
                },
              }))
            }
            options={[
              { label: 'POST', value: 'post' },
              { label: 'GET', value: 'get' },
            ]}
            data-testid="request-method-input"
          />

          <div>
            <label className="text-chariot-text-primary mb-2 block text-sm font-medium">
              Request Headers
            </label>
            {formData.dynamicConfig.requestHeaders.map((header, index) => (
              <div key={index} className="mb-2 flex gap-2">
                <input
                  type="text"
                  placeholder="Header name"
                  value={header.key}
                  onChange={e => {
                    onChange(prev => {
                      const newHeaders = [...prev.dynamicConfig.requestHeaders];
                      newHeaders[index].key = e.target.value;
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                  className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-[2px] border p-1.5 pl-3"
                />
                <input
                  type="text"
                  placeholder="Header value"
                  value={header.value}
                  onChange={e => {
                    onChange(prev => {
                      const newHeaders = [...prev.dynamicConfig.requestHeaders];
                      newHeaders[index].value = e.target.value;
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                  className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-[2px] border p-1.5 pl-3"
                />
                <Button
                  label=""
                  startIcon={<TrashIcon className="size-4" />}
                  onClick={() => {
                    onChange(prev => {
                      const newHeaders =
                        prev.dynamicConfig.requestHeaders.filter(
                          (_, i) => i !== index
                        );
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                  size="xs"
                  styleType="tertiary"
                />
              </div>
            ))}
            <Button
              label="Add Header"
              startIcon={<PlusIcon className="size-4" />}
              onClick={() => {
                onChange(prev => ({
                  ...prev,
                  dynamicConfig: {
                    ...prev.dynamicConfig,
                    requestHeaders: [
                      ...prev.dynamicConfig.requestHeaders,
                      { key: '', value: '' },
                    ],
                  },
                }));
              }}
              size="xs"
              styleType="tertiary"
              data-testid="add-header-button"
            />
          </div>

          <Input
            label="Request Body"
            name="requestBody"
            type={Type.TEXT_AREA}
            value={formData.dynamicConfig.requestBody}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  requestBody: e.target.value,
                },
              }))
            }
            placeholder='{"username": "user", "password": "pass"}'
            data-testid="request-body-input"
          />

          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                label="Refresh Interval"
                name="refreshInterval"
                value={formData.dynamicConfig.refreshInterval}
                onChange={e =>
                  onChange(prev => ({
                    ...prev,
                    dynamicConfig: {
                      ...prev.dynamicConfig,
                      refreshInterval: Number(e.target.value),
                    },
                  }))
                }
                placeholder="60"
                required
                data-testid="refresh-interval-input"
              />
            </div>
            <div className="flex-1">
              <Input
                label="Unit"
                name="refreshIntervalUnit"
                type={Type.SELECT}
                value={formData.dynamicConfig.refreshIntervalUnit}
                onChange={e =>
                  onChange(prev => ({
                    ...prev,
                    dynamicConfig: {
                      ...prev.dynamicConfig,
                      refreshIntervalUnit: e.target
                        .value as RefreshIntervalUnit,
                    },
                  }))
                }
                options={[
                  { label: 'Seconds', value: RefreshIntervalUnit.SECONDS },
                  { label: 'Minutes', value: RefreshIntervalUnit.MINUTES },
                  { label: 'Hours', value: RefreshIntervalUnit.HOURS },
                ]}
                data-testid="refresh-interval-unit-input"
              />
            </div>
          </div>

          <Input
            label="Token Location (JSON Path)"
            name="tokenLocation"
            value={formData.dynamicConfig.tokenLocation}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  tokenLocation: e.target.value,
                },
              }))
            }
            placeholder="access_token or data.token"
            required
            data-testid="token-location-input"
          />
        </div>
      )}
    </div>
  );
};

interface ApiKeyFormProps {
  formData: ApiKeyFormData;
  onChange: React.Dispatch<React.SetStateAction<ApiKeyFormData>>;
}

const ApiKeyForm: React.FC<ApiKeyFormProps> = ({ formData, onChange }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="text-chariot-text-primary mb-3 block text-sm font-medium">
          Choose your token type
        </label>
        <div className="flex gap-4">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="tokenType"
              checked={!formData.isDynamic}
              onChange={() => onChange(prev => ({ ...prev, isDynamic: false }))}
              className="text-chariot-brand-primary focus:ring-chariot-brand-primary"
            />
            <span className="text-chariot-text-primary text-sm">Fixed</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="tokenType"
              checked={formData.isDynamic}
              onChange={() => onChange(prev => ({ ...prev, isDynamic: true }))}
              className="text-chariot-brand-primary focus:ring-chariot-brand-primary"
            />
            <span className="text-chariot-text-primary text-sm">Dynamic</span>
          </label>
        </div>
      </div>

      {!formData.isDynamic ? (
        <div className="border-chariot-stroke space-y-4 rounded-lg border p-4">
          <h3 className="text-chariot-text-primary text-sm font-bold">
            Fixed token
          </h3>
          <p className="text-chariot-text-secondary text-sm">
            An access token that does not change. Manually configure the token.
          </p>

          <Input
            label="Label"
            name="label"
            value={formData.label}
            onChange={e =>
              onChange(prev => ({ ...prev, label: e.target.value }))
            }
            placeholder="Enter label"
            data-testid="api-key-label-input"
          />

          <Input
            label="Add to"
            name="addTo"
            type={Type.SELECT}
            value={formData.addTo}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                addTo: e.target.value as 'header' | 'query' | 'cookie',
              }))
            }
            options={[
              { label: 'Header', value: 'header' },
              { label: 'Query', value: 'query' },
              { label: 'Cookie', value: 'cookie' },
            ]}
            data-testid="add-to-input"
          />

          <Input
            label="Name"
            name="parameterName"
            value={formData.parameterName}
            onChange={e =>
              onChange(prev => ({ ...prev, parameterName: e.target.value }))
            }
            placeholder="X-API-Key or api_key"
            required
            data-testid="parameter-name-input"
          />

          <Input
            label="Key"
            name="apiKey"
            type={Type.PASSWORD}
            value={formData.apiKey}
            onChange={e =>
              onChange(prev => ({ ...prev, apiKey: e.target.value }))
            }
            placeholder="Enter API key"
            required
            data-testid="api-key-input"
          />
        </div>
      ) : (
        <div className="border-chariot-stroke space-y-4 rounded-lg border p-4">
          <h3 className="text-chariot-text-primary text-sm font-bold">
            Dynamic token
          </h3>
          <p className="text-chariot-text-secondary text-sm">
            Automatically retrieve and refresh the token from a service.
          </p>

          <Input
            label="Label"
            name="label"
            value={formData.label}
            onChange={e =>
              onChange(prev => ({ ...prev, label: e.target.value }))
            }
            placeholder="Enter label"
            data-testid="api-key-label-dynamic-input"
          />

          <Input
            label="Add to"
            name="addTo"
            type={Type.SELECT}
            value={formData.addTo}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                addTo: e.target.value as 'header' | 'query' | 'cookie',
              }))
            }
            options={[
              { label: 'Header', value: 'header' },
              { label: 'Query', value: 'query' },
              { label: 'Cookie', value: 'cookie' },
            ]}
            data-testid="add-to-dynamic-input"
          />

          <Input
            label="Name"
            name="parameterName"
            value={formData.parameterName}
            onChange={e =>
              onChange(prev => ({ ...prev, parameterName: e.target.value }))
            }
            placeholder="X-API-Key or api_key"
            required
            data-testid="parameter-name-dynamic-input"
          />

          <Input
            label="Token Service URL"
            name="tokenServiceUrl"
            value={formData.dynamicConfig.tokenServiceUrl}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  tokenServiceUrl: e.target.value,
                },
              }))
            }
            placeholder="https://api.example.com/token"
            required
            data-testid="token-service-url-dynamic-input"
          />

          <Input
            label="Request Method"
            name="requestMethod"
            type={Type.SELECT}
            value={formData.dynamicConfig.requestMethod}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  requestMethod: e.target.value as 'post' | 'get',
                },
              }))
            }
            options={[
              { label: 'POST', value: 'post' },
              { label: 'GET', value: 'get' },
            ]}
            data-testid="request-method-dynamic-input"
          />

          <div>
            <label className="text-chariot-text-primary mb-2 block text-sm font-medium">
              Request Headers
            </label>
            {formData.dynamicConfig.requestHeaders.map((header, index) => (
              <div key={index} className="mb-2 flex gap-2">
                <input
                  type="text"
                  placeholder="Header name"
                  value={header.key}
                  onChange={e => {
                    onChange(prev => {
                      const newHeaders = [...prev.dynamicConfig.requestHeaders];
                      newHeaders[index].key = e.target.value;
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                  className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-[2px] border p-1.5 pl-3"
                />
                <input
                  type="text"
                  placeholder="Header value"
                  value={header.value}
                  onChange={e => {
                    onChange(prev => {
                      const newHeaders = [...prev.dynamicConfig.requestHeaders];
                      newHeaders[index].value = e.target.value;
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                  className="border-chariot-stroke bg-chariot-background-primary flex-1 rounded-[2px] border p-1.5 pl-3"
                />
                <Button
                  label=""
                  startIcon={<TrashIcon className="size-4" />}
                  onClick={() => {
                    onChange(prev => {
                      const newHeaders =
                        prev.dynamicConfig.requestHeaders.filter(
                          (_, i) => i !== index
                        );
                      return {
                        ...prev,
                        dynamicConfig: {
                          ...prev.dynamicConfig,
                          requestHeaders: newHeaders,
                        },
                      };
                    });
                  }}
                  size="xs"
                  styleType="tertiary"
                />
              </div>
            ))}
            <Button
              label="Add Header"
              startIcon={<PlusIcon className="size-4" />}
              onClick={() => {
                onChange(prev => ({
                  ...prev,
                  dynamicConfig: {
                    ...prev.dynamicConfig,
                    requestHeaders: [
                      ...prev.dynamicConfig.requestHeaders,
                      { key: '', value: '' },
                    ],
                  },
                }));
              }}
              size="xs"
              styleType="tertiary"
              data-testid="add-header-button-dynamic"
            />
          </div>

          <Input
            label="Request Body"
            name="requestBody"
            type={Type.TEXT_AREA}
            value={formData.dynamicConfig.requestBody}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  requestBody: e.target.value,
                },
              }))
            }
            placeholder='{"username": "user", "password": "pass"}'
            data-testid="request-body-dynamic-input"
          />

          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                label="Refresh Interval"
                name="refreshInterval"
                value={formData.dynamicConfig.refreshInterval}
                onChange={e =>
                  onChange(prev => ({
                    ...prev,
                    dynamicConfig: {
                      ...prev.dynamicConfig,
                      refreshInterval: Number(e.target.value),
                    },
                  }))
                }
                placeholder="60"
                required
                data-testid="refresh-interval-dynamic-input"
              />
            </div>
            <div className="flex-1">
              <Input
                label="Unit"
                name="refreshIntervalUnit"
                type={Type.SELECT}
                value={formData.dynamicConfig.refreshIntervalUnit}
                onChange={e =>
                  onChange(prev => ({
                    ...prev,
                    dynamicConfig: {
                      ...prev.dynamicConfig,
                      refreshIntervalUnit: e.target
                        .value as RefreshIntervalUnit,
                    },
                  }))
                }
                options={[
                  { label: 'Seconds', value: RefreshIntervalUnit.SECONDS },
                  { label: 'Minutes', value: RefreshIntervalUnit.MINUTES },
                  { label: 'Hours', value: RefreshIntervalUnit.HOURS },
                ]}
                data-testid="refresh-interval-unit-dynamic-input"
              />
            </div>
          </div>

          <Input
            label="JSON Field Token Location"
            name="tokenLocation"
            value={formData.dynamicConfig.tokenLocation}
            onChange={e =>
              onChange(prev => ({
                ...prev,
                dynamicConfig: {
                  ...prev.dynamicConfig,
                  tokenLocation: e.target.value,
                },
              }))
            }
            placeholder="access_token or data.token"
            required
            data-testid="token-location-dynamic-input"
          />
        </div>
      )}
    </div>
  );
};

interface CredentialActionsProps {
  selectedCredentials: string[];
  onBulkDelete: () => void;
  hideActions?: boolean;
}

const CredentialActions: React.FC<CredentialActionsProps> = ({
  selectedCredentials,
  onBulkDelete,
  hideActions,
}) => {
  return (
    <Row className={cn('pb-0', hideActions && 'invisible')}>
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-bold">Actions:</h2>
        <Button
          label={`Delete ${selectedCredentials.length > 1 ? 'Credentials' : 'Credential'}`}
          startIcon={<TrashIcon className="size-5" />}
          onClick={onBulkDelete}
          size="xs"
          styleType="rounded"
          data-testid="bulk-delete-credentials"
        />
      </div>
    </Row>
  );
};
